import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { FileCheck, Handshake, CreditCard, Package, ShieldCheck } from "lucide-react";
import { useSectionTracking } from "./AnalyticsProvider";

const steps = [
  {
    num: "1",
    title: "Review & Verification",
    subtitle: "Verify the stone independently before any commitment.",
    icon: FileCheck,
    items: [
      { label: "Gubelin Gem Lab report", detail: "origin, species, weight, no treatment" },
      { label: "Independent gemological appraisal", detail: "Ringsrud Gemology" },
      { label: "Photos, video & live viewing", detail: "video call or in-person in Medellin" },
    ],
    note: "Verifiable with each source.",
  },
  {
    num: "2",
    title: "Agreement & Deposit",
    subtitle: "Sign a purchase agreement and place a deposit to secure the stone.",
    icon: Handshake,
    items: [
      { label: "Buyer completes KYC/AML", detail: "via third-party verification (e.g. Sumsub)" },
      { label: "Purchase agreement signed", detail: "clear terms for both parties" },
      { label: "Deposit placed", detail: "credited toward the purchase price, export costs to be determined" },
      { label: "Export process initiated", detail: "Colombian regulatory filings begin" },
    ],
    note: null,
  },
  {
    num: "3",
    title: "Final Payment & Release",
    subtitle: "Balance is paid within the agreed window. The emerald is released for secure shipment.",
    icon: CreditCard,
    items: [
      { label: "Balance payment completed", detail: "via agreed payment method" },
      { label: "Insured shipment arranged", detail: "professional logistics, fully insured, to your country's import facility" },
      { label: "Original documentation included", detail: "Gubelin report, appraisal, Gubelin profile book" },
    ],
    note: null,
  },
  {
    num: "4",
    title: "Delivery & Completion",
    subtitle: "The emerald arrives at your designated location with all documentation.",
    icon: Package,
    items: [
      { label: "Secure delivery", detail: "to your specified destination" },
      { label: "Import clearance", detail: "buyer completes local customs and duties (VAT)" },
    ],
    note: null,
  },
];

export default function PurchaseProcess() {
  const sectionRef = useSectionTracking("purchase_process");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-60px" });

  return (
    <section ref={sectionRef} className="relative py-12 sm:py-16 lg:py-20" data-testid="purchase-process-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-12" ref={animRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <p className="overline mb-4">Purchase Process</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-4">
            A Clear Path from <span className="italic text-[#C5A059]">Verification to Delivery</span>
          </h2>
          <p className="text-base text-[#B8B3AA]/75 max-w-2xl mx-auto font-light leading-relaxed">
            Every step is designed around transparency, security, and the expectations of discerning collectors.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-6 lg:space-y-8">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.12 }}
                className="relative p-6 sm:p-8 lg:p-10 border border-[#C5A059]/10 bg-[#14130F]"
                data-testid={`purchase-step-${i}`}
              >
                <div className="flex flex-col sm:flex-row gap-5 sm:gap-8">
                  {/* Step number + icon */}
                  <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-3 flex-shrink-0">
                    <span className="font-serif text-4xl lg:text-5xl text-[#C5A059]/20">{step.num}</span>
                    <Icon size={20} strokeWidth={1.5} className="text-[#C5A059]/40" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-serif text-xl lg:text-2xl text-[#F4F0EA] mb-2">{step.title}</h3>
                    <p className="text-base text-[#B8B3AA]/75 font-light mb-5">{step.subtitle}</p>

                    {step.note && (
                      <p className="text-xs text-[#C5A059]/50 uppercase tracking-wider mb-4">{step.note}</p>
                    )}

                    <div className="space-y-3">
                      {step.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-3">
                          <div className="w-1 h-1 bg-[#C5A059]/40 rounded-full mt-2.5 flex-shrink-0" />
                          <div>
                            <span className="text-sm text-[#F4F0EA]/75 font-medium">{item.label}</span>
                            <span className="text-sm text-[#B8B3AA]/75"> — {item.detail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Escrow note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 p-5 sm:p-6 border border-[#C5A059]/15 bg-[#C5A059]/[0.03] flex items-start gap-4"
          data-testid="escrow-note"
        >
          <ShieldCheck size={20} strokeWidth={1.5} className="text-[#C5A059]/50 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#B8B3AA]/75 leading-relaxed">
            <span className="text-[#C5A059]/70 font-medium">Escrow services are available.</span> Payment method, logistics, and insurance details are confirmed during the agreement stage.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
